import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '../../modules/api-response';
import { BaseApiService } from '../../core/base-api.service';
import { FeedResource, FeedsByProvider } from '../models/feed.model';
import { FeedDiagnostics } from '../models/feed-diagnostics.model';

/** Tenant-facing feed listing — every feed belonging to the caller's own
 *  tenant, across all provider types, grouped by provider name. Role: viewer. */
@Injectable({ providedIn: 'root' })
export class FeedService extends BaseApiService {
  private readonly http = inject(HttpClient);
  private readonly feedsUrl = `${this.coreApiUrl}/api/v1/feeds`;

  getAll(): Observable<ApiResponse<FeedsByProvider>> {
    return this.http.get<ApiResponse<FeedsByProvider>>(this.feedsUrl);
  }

  /** Resources (e.g. stops) the feed's provider knows about, for picking by name.
   *  Caller's own feeds only. Providers without resource discovery (e.g. siri)
   *  return [], as does a provider with no data yet; a failing provider gives
   *  502 — treat all of them as "enter ids manually", never as a blocking error. */
  getResources(feedId: string): Observable<ApiResponse<FeedResource[]>> {
    return this.http.get<ApiResponse<FeedResource[]>>(`${this.feedsUrl}/${feedId}/resources`);
  }

  /** Latest diagnostics snapshot for one of the caller's own feeds. Tenant is
   *  resolved from the JWT, not the URL — 404 if none exists yet, or if
   *  feedId belongs to another tenant (indistinguishable from the former).
   *  `payload` is provider-specific JSON; pass e.g. `<SiriDiagnosticsPayload>`
   *  when the caller knows the provider type, otherwise it comes back `unknown`. */
  getDiagnostics<TPayload = unknown>(feedId: string): Observable<ApiResponse<FeedDiagnostics<TPayload>>> {
    return this.http.get<ApiResponse<FeedDiagnostics<TPayload>>>(`${this.feedsUrl}/${feedId}/diagnostics`);
  }
}
